(() => {
  // The snippet can appear more than once, including in theme-editor replacements.
  if (customElements.get('aven-featured-reviews')) return;

  class AvenFeaturedReviews extends HTMLElement {
    constructor() {
      super();
      this.mount = this.mount.bind(this);
      this.scheduleHeight = this.scheduleHeight.bind(this);
    }

    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      // Defer until this element's children have been parsed. A load fallback also
      // handles an async theme script; no polling or global section-load listener.
      window.addEventListener('load', this.mount, { once: true });
      this.mountFrame = requestAnimationFrame(this.mount);
    }

    mount() {
      if (!this.connected || !this.isConnected || this.instance) return;
      if (typeof window.Splide !== 'function') return;
      const slider = this.querySelector('.splide');
      const list = slider && slider.querySelector('.splide__list');
      if (!list) return;
      this.slides = Array.from(list.children).filter(slide =>
        slide.classList.contains('splide__slide') && !slide.classList.contains('splide__slide--clone')
      );
      if (this.slides.length < 2) return;

      window.removeEventListener('load', this.mount);
      const type = ['loop', 'slide', 'fade'].includes(this.dataset.type) ? this.dataset.type : 'loop';
      const interval = Number(this.dataset.autoplaySpeed);
      const rtl = document.querySelector('script[data-is-rtl="true"]') || document.documentElement.dir === 'rtl';
      this.instance = new window.Splide(slider, {
        type,
        rewind: type !== 'loop',
        perPage: 1,
        perMove: 1,
        gap: '20px',
        padding: 0,
        direction: rtl ? 'rtl' : 'ltr',
        arrows: this.dataset.arrows === 'true',
        pagination: this.dataset.pagination === 'true',
        autoplay: this.dataset.autoplay === 'true',
        // This block's Shopify setting is already milliseconds, not seconds.
        interval: Number.isFinite(interval) && interval >= 1000 ? interval : 4000,
        pauseOnHover: true,
        pauseOnFocus: true,
        reducedMotion: { speed: 0, rewindSpeed: 0, autoplay: 'pause' }
      });
      this.instance.on('mounted moved resized updated', this.scheduleHeight);
      this.instance.mount();
      this.addEventListener('load', this.scheduleHeight, true);
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(this.scheduleHeight);
        this.slides.forEach(slide => this.resizeObserver.observe(slide));
      }
      this.scheduleHeight();
    }

    scheduleHeight() {
      if (!this.connected || !this.instance || this.heightFrame) return;
      this.heightFrame = requestAnimationFrame(() => {
        this.heightFrame = null;
        if (!this.connected || !this.instance) return;
        const slide = this.slides[this.instance.index];
        const height = slide && slide.offsetHeight;
        if (height > 0) this.style.setProperty('--active-slide-height', `${height}px`);
      });
    }

    disconnectedCallback() {
      this.connected = false;
      window.removeEventListener('load', this.mount);
      this.removeEventListener('load', this.scheduleHeight, true);
      cancelAnimationFrame(this.mountFrame);
      cancelAnimationFrame(this.heightFrame);
      this.mountFrame = this.heightFrame = null;
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.resizeObserver = null;
      // Full destruction removes Splide's clones, pagination and event handlers.
      if (this.instance) this.instance.destroy(true);
      this.instance = null;
      this.slides = null;
      this.style.removeProperty('--active-slide-height');
    }
  }

  customElements.define('aven-featured-reviews', AvenFeaturedReviews);
})();
